import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines, EASE } from "@/components/Motion";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const GUIDE = [
  ["Audience and reach", "We start from the size of the audience you can reach: attendees at your event, people who see your media, and your social followers. Each one is priced at typical industry cost-per-thousand (CPM) rates for that channel."],
  ["Engagement", "A smaller, engaged audience is often worth more than a large passive one. Your engagement rate lifts or lowers the value so that active communities are priced fairly."],
  ["Demographics and geography", "Premium audiences, such as luxury buyers or senior decision makers, and high-value markets carry higher multipliers than mass-market or local reach."],
  ["Exclusivity and deliverables", "Category or full exclusivity raises the price because the partner blocks out competitors. Each deliverable you list, such as a VIP lounge, naming rights or creator posts, adds to the total."],
];
const FAQ = [
  ["How accurate is the estimate?", "It gives a market-rate range based on common benchmarks. Use it as a starting point for negotiation, then adjust for your brand strength, past results and the partner's goals."],
  ["Who is it for?", "Event organisers, creators, venues, sports teams, publishers and nonprofits pricing a sponsorship, and brands checking whether a proposal is fair."],
  ["Is it free?", "Yes. Anyone can use the calculator without an account."],
];

const NUMS = [["audience_size", "Audience size", "500000"], ["media_reach", "Media reach (impressions)", "3000000"], ["social_followers", "Social followers", "1200000"], ["event_attendance", "Event attendance", "800"], ["engagement", "Engagement rate %", "4.2"]];
const usd = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function Valuation() {
  const [opts, setOpts] = useState({ demographics: [], geography: [], exclusivity: [] });
  const [f, setF] = useState({ audience_size: "", media_reach: "", social_followers: "", event_attendance: "", engagement: "", demographics: "Mass market", geography: "Global", exclusivity: "None", deliverables: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo("Partnership Valuation Calculator", "Estimate what your sponsorship or brand partnership is worth from audience size, media reach, engagement, demographics, geography and exclusivity.", "/valuation");
  useEffect(() => { api.get("/valuation/options").then((r) => setOpts(r.data || opts)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...f, deliverables: f.deliverables.split(",").map((d) => d.trim()).filter(Boolean) };
      NUMS.forEach(([k]) => { body[k] = Number(f[k]) || 0; });
      if (!f.engagement) body.engagement = 3;
      const { data } = await api.post("/valuation", body);
      setRes(data);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const Sel = ({ k, label }) => (
    <label className="block text-xs uppercase tracking-[0.14em] text-slate-400">{label}
      <select className="field mt-2 normal-case tracking-normal" value={f[k]} onChange={set(k)} data-testid={`val-${k}-select`}>{(opts[k] || []).map((o) => <option key={o}>{o}</option>)}</select>
    </label>
  );
  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="valuation-page">
      <Nav />
      <section className="pt-32 pb-24 mx-auto max-w-7xl px-5 sm:px-8">
        <p className="kicker">Partnership valuation</p>
        <MaskedLines as="h1" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight" lines={["What is your", <em key="v" className="italic gold-text">opportunity worth?</em>]} />
        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 rounded-3xl border border-[#2A1240]/10 bg-[#FFFFFF] p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              {NUMS.map(([k, label, ph]) => (
                <label key={k} className="block text-xs uppercase tracking-[0.14em] text-slate-400">{label}
                  <input type="number" min={0} className="field mt-2 normal-case tracking-normal font-mono" placeholder={ph} value={f[k]} onChange={set(k)} data-testid={`val-${k}-input`} />
                </label>
              ))}
              {Sel({ k: "demographics", label: "Demographics" })}
              {Sel({ k: "geography", label: "Geography" })}
              {Sel({ k: "exclusivity", label: "Exclusivity" })}
            </div>
            <label className="mt-5 block text-xs uppercase tracking-[0.14em] text-slate-400">Deliverables (comma separated)
              <input className="field mt-2 normal-case tracking-normal" placeholder="Runway, VIP lounge, 3 creator posts" value={f.deliverables} onChange={set("deliverables")} data-testid="val-deliverables-input" />
            </label>
            {err && <p className="mt-4 text-sm text-red-300" data-testid="val-error">{err}</p>}
            <button className="btn-gold mt-8 w-full sm:w-auto" disabled={busy} onClick={run} data-testid="val-estimate-button"><Sparkles className="h-4 w-4" />{busy ? "Estimating…" : "Estimate value"}</button>
          </div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 rounded-3xl border border-[#E0218A]/30 bg-gradient-to-b from-[#FBE9F3] to-[#FFFFFF] p-8 min-h-[320px]" data-testid="val-result">
              <p className="kicker">Estimated partnership value</p>
              <AnimatePresence>
                {res ? (
                  <motion.div key={`${res.low}-${res.high}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                    <p className="mt-6 font-display text-4xl sm:text-5xl gold-text leading-tight" data-testid="val-range">{usd(res.low)}–{usd(res.high)}</p>
                    <div className="mt-8 space-y-3">
                      {(res.breakdown || []).map((b) => {
                        const pct = res.mid ? Math.min((b.value / res.mid) * 100, 100) : 0;
                        return (
                          <div key={b.label}>
                            <div className="flex justify-between text-sm"><span className="text-slate-300">{b.label}</span><span className="font-mono text-[#2A1240]">{usd(b.value)}</span></div>
                            <div className="mt-1.5 h-1 rounded-full bg-[#2A1240]/10 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: EASE }} className="h-full bg-[#E0218A]" /></div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-6 text-xs text-slate-400">Multipliers · demographics ×{res.multipliers?.demographics} · geography ×{res.multipliers?.geography} · exclusivity ×{res.multipliers?.exclusivity}</p>
                  </motion.div>
                ) : <p className="mt-6 text-slate-300">Fill in what you know — audience, reach, followers, attendance — and get an instant market-rate range.</p>}
              </AnimatePresence>
              <p className="mt-8 text-[11px] text-slate-400">Indicative estimate based on industry CPM and engagement benchmarks. Not a guarantee of price.</p>
            </div>
          </div>
        </div>
        <p className="mt-16 max-w-3xl text-slate-300" data-testid="val-intro">The Balqis Book partnership valuation calculator helps marketing and partnerships teams put a fair price on a sponsorship, collaboration or brand activation. Enter the numbers you know and get an indicative range in seconds, with a breakdown of where the value comes from.</p>
        <div className="mt-12 grid md:grid-cols-2 gap-8" data-testid="val-guide">
          {GUIDE.map(([h, t]) => (
            <div key={h} className="border-t border-[#2A1240]/10 pt-6">
              <h2 className="font-display text-base md:text-lg text-[#2A1240]">{h}</h2>
              <p className="mt-2 text-sm sm:text-base text-slate-300">{t}</p>
            </div>
          ))}
        </div>
        <div className="mt-16" data-testid="val-faq">
          <h2 className="kicker">Frequently asked questions</h2>
          <dl className="mt-6 space-y-6 max-w-3xl">
            {FAQ.map(([q, a]) => (
              <div key={q}><dt className="text-[#2A1240]">{q}</dt><dd className="mt-1 text-sm sm:text-base text-slate-300">{a}</dd></div>
            ))}
          </dl>
        </div>
      </section>
      <Footer />
    </div>
  );
}
